/**
 * Générateur — Automatisme : utiliser la propriété du triangle rectangle et de
 * son cercle circonscrit.
 * Attendu ID : 1242 | Niveau : 3ème | Domaine : Espace et géométrie, Triangles
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 l. 1097-1101 ; reprend les
 * objectifs 4e l. 1046-1048 sans gen en base).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTRACTION de gen_956 (objectif 5e) avec une FIGURE ajoutée (figTriangle.js :
 * le triangle rectangle dans son cercle, le centre O au milieu de
 * l'hypoténuse) : diamètre (QCM 3 choix), rayon (UN trou, sens tiré, règle
 * retirée de la consigne), propriétés (V/F filtré au triangle rectangle + une
 * ligne numérique), « l'hypoténuse est un … » (« corde » retiré : vrai aussi).
 * Sens RÉCIPROQUE neuf : « Est-il rectangle ? » (triangle inscrit, un côté
 * passe ou non par O ; en QCM sur figure, À ARBITRER). Placer le centre
 * écarté (objectif 4e). Plages : hypoténuse {4, 6, 8, 12} et {5, 7, 9} une
 * fois sur trois ; rayon {2 ; 2,5 ; 3 ; 3,5 ; 4 ; 4,5 ; 6}.
 * Banque MathsMentales 6GD4 relevée (cercle de diamètre un côté → oui).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Quel segment est un diamètre ? » (diametre_qcm_unique) : [DF]
 *     parmi [DE], [EF] (rectangle en E).
 *   Carte 2 « Rayon et hypoténuse » (rayon_texte_trou_saisie) : « DEF est
 *     rectangle en E et DF = 8 cm : le rayon du cercle circonscrit vaut ⬚
 *     cm » ou « … cercle de rayon 3,5 cm : son hypoténuse mesure ⬚ cm ».
 *   Carte 3 « L'hypoténuse est… » (rectangle_qcm_unique) : [un diamètre ; un
 *     rayon ; un axe de symétrie].
 *   Carte 4 « Est-il rectangle ? » (reciproque_qcm_unique) : triangle inscrit,
 *     centre O marqué ; [Oui, en E ; Oui, en D ; Non].
 *   Carte 5 « Vrai ou faux » (proprietes_tableau_vf) : le centre, le cercle,
 *     une ligne numérique, la réciproque.
 *
 * INVARIANTS : figures avec data-figure (le centre O y figure) ; le triangle
 * inscrit de la carte 4 a un côté diamètre ou aucun (écart angulaire ≥ 40°) ;
 * distracteurs distincts ; lettres saines.
 * SACRIFIÉ (fiche : le cercle de rayon 5 de 956) : (hypoténuse 10 ; rayon 5),
 * jamais tiré ; auto-testé.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   rayon_pour_diametre   : rayon 8 pour une hypoténuse de 8, PRINCIPALE ;
 *   sens_inverse          : hypoténuse 1,75 pour un rayon 3,5 (moitié au lieu du double) ;
 *   cote_angle_droit      : [DE] pris pour le diamètre ;
 *   centre_au_sommet_droit : le centre du cercle au sommet de l'angle droit ;
 *   reciproque_abusive    : tout triangle inscrit serait rectangle ;
 *   centre_interieur      : le centre toujours dans le triangle ;
 *   mauvais_sommet        : rectangle au mauvais sommet (réciproque).
 */
import {
    randomChoice, randInt, shuffle, pickLettresSains, tex,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgTriangle, tourner, milieu } from './figTriangle';

const ATTENDU_ID = 1242;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DIAMETRE = 'Quel segment est un diamètre ?';
const L_RAYON = 'Rayon et hypoténuse';
const L_HYP = 'L\'hypoténuse est…';
const L_RECIPROQUE = 'Est-il rectangle ?';
const L_VF = 'Vrai ou faux';

const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
/** Sacrifié (fiche) : hypoténuse 10 cm, rayon 5 cm. */
export const SACRIFIE = { hyp: 10, rayon: 5 };
const HYPS = [4, 6, 8, 12]; const HYPS_IMPAIRES = [5, 7, 9];
const RAYONS = [2, 2.5, 3, 3.5, 4, 4.5, 6];
const fr = (x) => String(x).replace('.', ',');

const EXPLICATIONS = {
    regle: 'Dans un triangle rectangle, l\'hypoténuse est un diamètre du cercle circonscrit : le centre du cercle est le milieu de l\'hypoténuse et le rayon en est la moitié.',
    rayon_pour_diametre: 'L\'hypoténuse est un DIAMÈTRE : le rayon en est la moitié.',
    sens_inverse: 'Le rayon est la moitié de l\'hypoténuse, donc l\'hypoténuse est le DOUBLE du rayon.',
    cote_angle_droit: 'Le diamètre, c\'est l\'hypoténuse : le côté en face de l\'angle droit, pas un côté de l\'angle droit.',
    centre_au_sommet_droit: 'Le centre est au milieu de l\'hypoténuse, pas au sommet de l\'angle droit.',
    reciproque_abusive: 'Un triangle inscrit dans un cercle est rectangle seulement si l\'un de ses côtés est un diamètre.',
    centre_interieur: 'Pour un triangle rectangle, le centre est SUR l\'hypoténuse ; il peut aussi être à l\'extérieur.',
    mauvais_sommet: 'L\'angle droit est en face du diamètre : au sommet qui n\'est pas sur le diamètre.',
};

// ─── Figures ──────────────────────────────────────────────────────────────

/** Triangle rectangle nommé dans son cercle : rectangle en E, hypoténuse [DF], centre O milieu de [DF]. */
const tirerRectangle = () => {
    const [D, E, F] = pickLettresSains(POOL, 3);
    const a = randInt(4, 7); const b = randInt(2, 5);
    const pts = tourner([[0, 0], [a, 0], [0, b]], randomChoice([0, 45, 90, 135, 180, 225, 270, 315]));
    const points = { [E]: pts[0], [D]: pts[1], [F]: pts[2], O: milieu(pts[1], pts[2]) };
    return { D, E, F, points, nom: `${D}${E}${F}` };
};
const figureRectangle = (t) => imgElement(svgTriangle({
    points: t.points, triangle: [t.D, t.E, t.F], segments: [[t.D, t.E], [t.E, t.F], [t.F, t.D]], angleDroit: t.E, cercle: { centre: 'O', par: t.D },
}, `Le triangle ${t.nom}, rectangle en ${t.E}, dans son cercle circonscrit de centre O.`), 'large');

/** Triangle inscrit : rectangle (un côté diamètre) ou non (aucune paire de sommets opposés). */
const tirerInscrit = (rectangle) => {
    const [X, Y, Z] = pickLettresSains(POOL, 3);
    const R = 5;
    let angles;
    for (let essai = 0; essai < 100; essai++) {
        const a1 = randInt(0, 35) * 10;
        if (rectangle) {
            const a3 = a1 + 180; const a2 = a1 + randInt(4, 14) * 10;   // Y entre X et Z, à 40° au moins de chacun
            angles = [a1, a2, a3];
        } else {
            const a2 = a1 + randInt(5, 14) * 10; const a3 = a2 + randInt(5, 14) * 10;
            const ecarts = [a2 - a1, a3 - a2, 360 - (a3 - a1)];
            if (ecarts.some((e) => Math.abs(e - 180) < 40) || ecarts.some((e) => e < 40)) continue;   // aucune corde proche d'un diamètre
            angles = [a1, a2, a3];
        }
        break;
    }
    const P = (deg) => [R * Math.cos((deg * Math.PI) / 180), R * Math.sin((deg * Math.PI) / 180)];
    const points = { [X]: P(angles[0]), [Y]: P(angles[1]), [Z]: P(angles[2]), O: [0, 0] };
    return { X, Y, Z, rectangle, points, nom: `${X}${Y}${Z}` };
};
const figureInscrit = (t) => imgElement(svgTriangle({
    points: t.points, triangle: [t.X, t.Y, t.Z], segments: [[t.X, t.Y], [t.Y, t.Z], [t.Z, t.X]], cercle: { centre: 'O', par: t.X },
}, `Le triangle ${t.nom} inscrit dans un cercle de centre O.`), 'large');

const seg = (a, b) => tex(`[${a}${b}]`);

// ─── Carte 1 : quel segment est un diamètre ? (QCM 3 choix) ──────────────

const genDiametre = () => {
    const t = tirerRectangle();
    const bon = seg(t.D, t.F);
    const faux = shuffle([{ v: seg(t.D, t.E), code: 'cote_angle_droit' }, { v: seg(t.E, t.F), code: 'cote_angle_droit' }]);
    const texte = `<p>${tex(t.nom)} est un triangle rectangle en ${tex(t.E)}. On a tracé son cercle circonscrit.</p><p><b>Quel segment est un diamètre de ce cercle ?</b></p>`;
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, texte, { ...opts(L_DIAMETRE), elements_additionnels: figureRectangle(t) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { D: t.D, E: t.E, F: t.F, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 2 : rayon et hypoténuse (un trou, sens tiré) ──────────────────

const tirerHyp = () => { for (;;) { const h = randInt(1, 3) === 1 ? randomChoice(HYPS_IMPAIRES) : randomChoice(HYPS); if (h !== SACRIFIE.hyp) return h; } };
const tirerRayon = () => { for (;;) { const r = randomChoice(RAYONS); if (r !== SACRIFIE.rayon) return r; } };

const genRayon = () => {
    const t = tirerRectangle();
    const direct = randInt(0, 1) === 1;
    let phrase; let params;
    if (direct) {
        const h = tirerHyp();
        phrase = `${tex(t.nom)} est rectangle en ${tex(t.E)} et ${tex(`${t.D}${t.F} = ${h}`)} cm : le rayon de son cercle circonscrit vaut {{${fr(h / 2)}}} cm.`;
        params = { sens: 'hyp_vers_rayon', hyp: h, rayon: h / 2 };
    } else {
        const r = tirerRayon();
        phrase = `${tex(t.nom)} est rectangle en ${tex(t.E)} et inscrit dans un cercle de rayon ${fr(r)} cm : son hypoténuse ${tex(`${t.D}${t.F}`)} mesure {{${fr(2 * r)}}} cm.`;
        params = { sens: 'rayon_vers_hyp', hyp: 2 * r, rayon: r };
    }
    const payload = makeTexteTrou(phrase, '<p><b>Complète.</b></p>', { ...opts(L_RAYON), mode: 'saisie', strict: false, elements_additionnels: figureRectangle(t) });
    payload.contenu = { ...payload.contenu, sousType: params.sens, explications: EXPLICATIONS };
    payload._meta.params = { D: t.D, E: t.E, F: t.F, ...params };
    return payload;
};

// ─── Carte 3 : l'hypoténuse est… (QCM 3 choix) ───────────────────────────

const genHyp = () => {
    const t = tirerRectangle();
    const bon = 'un diamètre';
    const faux = shuffle([{ v: 'un rayon', code: 'rayon_pour_diametre' }, { v: 'un axe de symétrie', code: 'axe' }]);
    const texte = `<p>${tex(t.nom)} est un triangle rectangle en ${tex(t.E)}. On a tracé son cercle circonscrit.</p><p><b>L'hypoténuse ${seg(t.D, t.F)} est… de ce cercle.</b></p>`;
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, texte, { ...opts(L_HYP), elements_additionnels: figureRectangle(t) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { D: t.D, E: t.E, F: t.F, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 4 : est-il rectangle ? (QCM 3 choix, réciproque) ──────────────

const genReciproque = () => {
    const t = tirerInscrit(randInt(0, 1) === 1);
    // Les choix portent la raison : c'est la réciproque qu'on installe (retour prof 13/09).
    const oui = (s) => { const autres = [t.X, t.Y, t.Z].filter((l) => l !== s).sort(); return `Oui, rectangle en ${tex(s)} : ${tex(`[${autres[0]}${autres[1]}]`)} est un diamètre.`; };
    const non = 'Non : aucun côté n\'est un diamètre.';
    let bon; let faux;
    if (t.rectangle) { bon = oui(t.Y); faux = [{ v: oui(randomChoice([t.X, t.Z])), code: 'mauvais_sommet' }, { v: non, code: 'diametre_non_vu' }]; }
    else { bon = non; const [s1, s2] = shuffle([t.X, t.Y, t.Z]).slice(0, 2); faux = [{ v: oui(s1), code: 'reciproque_abusive' }, { v: oui(s2), code: 'reciproque_abusive' }]; }
    const texte = `<p>Le triangle ${tex(t.nom)} est inscrit dans un cercle de centre ${tex('O')}.</p><p><b>Est-il rectangle ?</b> Regarde si un côté passe par ${tex('O')}.</p>`;
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, texte, { ...opts(L_RECIPROQUE), elements_additionnels: figureInscrit(t) });
    payload.contenu = { ...payload.contenu, sousType: t.rectangle ? 'rectangle' : 'quelconque', explications: EXPLICATIONS };
    payload._meta.params = { X: t.X, Y: t.Y, Z: t.Z, rectangle: t.rectangle, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 5 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    const t = tirerRectangle();
    const h = tirerHyp();
    const candidats = [
        { l: `Le centre du cercle circonscrit est le milieu de ${seg(t.D, t.F)}.`, v: true, fam: 'centre' },
        { l: `Le centre du cercle circonscrit est le point ${tex(t.E)}.`, v: false, fam: 'centre', code: 'centre_au_sommet_droit' },
        { l: 'Le centre du cercle circonscrit est toujours à l\'intérieur du triangle.', v: false, fam: 'centre', code: 'centre_interieur' },
        { l: `${seg(t.D, t.F)} est un diamètre du cercle circonscrit.`, v: true, fam: 'diametre' },
        { l: `${seg(t.D, t.E)} est un diamètre du cercle circonscrit.`, v: false, fam: 'diametre', code: 'cote_angle_droit' },
        { l: `Si ${tex(`${t.D}${t.F} = ${h}`)} cm, le rayon du cercle circonscrit mesure ${fr(h / 2)} cm.`, v: true, fam: 'numerique' },
        { l: `Si ${tex(`${t.D}${t.F} = ${h}`)} cm, le rayon du cercle circonscrit mesure ${h} cm.`, v: false, fam: 'numerique', code: 'rayon_pour_diametre' },
        { l: `Si ${tex(`${t.D}${t.F} = ${h}`)} cm, le rayon du cercle circonscrit mesure ${2 * h} cm.`, v: false, fam: 'numerique', code: 'sens_inverse' },
        { l: 'Un triangle inscrit dans un cercle est toujours rectangle.', v: false, fam: 'reciproque', code: 'reciproque_abusive' },
        { l: 'Si un côté d\'un triangle inscrit dans un cercle est un diamètre, ce triangle est rectangle.', v: true, fam: 'reciproque' },
    ];
    for (let essai = 0; essai < 100; essai++) {
        const nbFaux = randInt(1, 3);
        const fausses = shuffle(candidats.filter((x) => !x.v)); const vraies = shuffle(candidats.filter((x) => x.v));
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((x) => x.fam)).size !== 4) continue;
        if (!lignes.some((x) => x.fam === 'reciproque')) continue;   // la réciproque toujours présente (retour prof)
        const texte = `<p>${tex(t.nom)} est un triangle rectangle en ${tex(t.E)}.</p><p><b>Vrai ou faux ?</b></p>`;
        const payload = makeVraiFaux(lignes.map((x) => x.l), lignes.map((x) => (x.v ? 0 : 1)), texte, { ...opts(L_VF), elements_additionnels: figureRectangle(t) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { D: t.D, E: t.E, F: t.F, hyp: h, distracteurs: lignes.filter((x) => x.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1242 : V/F sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'figure toujours présente (le triangle dans son cercle, O au milieu de l\'hypoténuse) ; lettres saines ; hypoténuse {4, 6, 8, 12} et {5, 7, 9} une fois sur trois ; rayon {2 ; 2,5 ; 3 ; 3,5 ; 4 ; 4,5 ; 6}',
        'sacrifié (hypoténuse 10 ; rayon 5) ; « corde » retiré des choix (vrai aussi)',
    ],
    diametre_qcm_unique: 'cote_angle_droit : les deux côtés de l\'angle droit (trois choix)',
    rectangle_qcm_unique: 'rayon_pour_diametre, axe de symétrie (trois choix)',
    reciproque_qcm_unique: 'rectangle : mauvais_sommet, diametre_non_vu ; quelconque : reciproque_abusive (deux sommets)',
    proprietes_tableau_vf: 'quatre familles exclusives : le centre, le diamètre, la ligne numérique, la réciproque',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    diametre_qcm_unique: genDiametre,
    rayon_texte_trou_saisie: genRayon,
    rectangle_qcm_unique: genHyp,
    reciproque_qcm_unique: genReciproque,
    proprietes_tableau_vf: genVf,
};

export const CATEGORIES = [
    // la réciproque en tête : c'est elle qu'il faut installer (retour prof 13/09)
    { id: 'reciproque', label: L_RECIPROQUE, formatsList: [{ key: 'reciproque_qcm_unique', label: 'QCM' }] },
    { id: 'diametre', label: L_DIAMETRE, formatsList: [{ key: 'diametre_qcm_unique', label: 'QCM' }] },
    { id: 'rayon', label: L_RAYON, formatsList: [{ key: 'rayon_texte_trou_saisie', label: 'Saisie' }] },
    { id: 'rectangle', label: L_HYP, formatsList: [{ key: 'rectangle_qcm_unique', label: 'QCM' }] },
    { id: 'proprietes', label: L_VF, formatsList: [{ key: 'proprietes_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
